import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";

type Theme = "light" | "dark";

const STORAGE_KEY = "nicegift-theme";
const TRANSITION_MS = 500;

const getUserStorageKey = (userId: string) => `${STORAGE_KEY}:${userId}`;

export const useTheme = () => {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return "light";
    return document.documentElement.classList.contains("dark") ? "dark" : "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = user ? (localStorage.getItem(getUserStorageKey(user.id)) as Theme | null) : null;
    setThemeState(stored === "dark" || stored === "light" ? stored : "light");
  }, [user?.id]);

  const toggleTheme = useCallback(() => {
    // Create a full-screen overlay for smooth fade transition
    const overlay = document.createElement("div");
    Object.assign(overlay.style, {
      position: "fixed",
      inset: "0",
      zIndex: "99999",
      background: theme === "dark" ? "white" : "#0d1520",
      opacity: "0",
      pointerEvents: "none",
      transition: `opacity ${TRANSITION_MS / 2}ms ease-in-out`,
    });
    document.body.appendChild(overlay);

    // Fade in overlay
    requestAnimationFrame(() => {
      overlay.style.opacity = "0.6";
    });

    // At peak, switch theme
    setTimeout(() => {
      setThemeState((prev) => {
        const next = prev === "dark" ? "light" : "dark";
        if (user) localStorage.setItem(getUserStorageKey(user.id), next);
        return next;
      });

      // Fade out overlay
      requestAnimationFrame(() => {
        overlay.style.opacity = "0";
      });

      // Remove overlay after fade out
      setTimeout(() => {
        overlay.remove();
      }, TRANSITION_MS / 2);
    }, TRANSITION_MS / 2);
  }, [theme, user]);

  return { theme, toggleTheme, isDark: theme === "dark" };
};
